import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { ProductStockDTO, ProductStockFilter, UpdateStockSettings } from '../../types/inventory/ProductStockTypes';
import InventoryStockService from '../../services/inventory/InventoryStockService';
import { clearStockList, setIsLoading, setStockList, closeEditModal, openEditModal, updateStockSettingsInList } from '../../store/slices/inventory/inventoryStockSlice';
import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import useApiErrorHandler from '../useApiErrorHandler';

export const useInventoryStock = () => {
    const dispatch: AppDispatch = useDispatch();
    const { show: showAlert } = useAlerts();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
    const { handle } = useApiErrorHandler();

    const { list, paginator, isLoading, isEditModalOpen, selectedStock } = useSelector(
        (state: RootState) => state.inventoryStock
    );

    /**
     * Carga el stock filtrado con paginación.
     */
    const handlerGetFilteredStock = async (
        filters: ProductStockFilter,
        page = 0,
        size = 10,
        sort = 'product.name,asc' // 🔍 orden default: por nombre de producto
    ) => {
        try {
            dispatch(setIsLoading(true));
            showLoadingIndicator();

            const response = await InventoryStockService.getFiltered(filters, page, size, sort);

            dispatch(setStockList({ content: response.content, paginator: response }));
        } catch (error) {
            handle(error);
            showAlert('error', 'Error', 'No se pudo obtener el stock.');
        } finally {
            dispatch(setIsLoading(false));
            hideLoadingIndicator();
        }
    };

    const handlerUpdateStockSettings = async (updateData: UpdateStockSettings) => {
        try {
            showLoadingIndicator();

            const response = await InventoryStockService.updateSettings(updateData);
            const { statusCode, title, message, type, data } = response;

            if (statusCode === 200 && data) {
                // 🔥 Actualiza en la lista usando el response
                dispatch(updateStockSettingsInList({
                    productId: data.productId,
                    branchId: data.branchId,
                    minStock: data.minStock,
                    reorderPoint: data.reorderPoint,
                }));

                showAlert(type, title, message);
            } else {
                showAlert(type || 'error', title || 'Error', message || 'Ocurrió un error.');
            }
        } catch (error) {
            handle(error);
            showAlert('error', 'Error', 'No se pudo actualizar el stock mínimo o punto de reorden.');
        } finally {
            hideLoadingIndicator();
        }
    };


    /**
     * Limpia el stock listado y resetea paginador.
     */
    const handlerClearFilteredStock = () => {
        dispatch(clearStockList());
    };

    /**
 * Abre el modal de edición y establece el producto seleccionado.
 */
    const handlerOpenEditModal = (stock: ProductStockDTO) => {
        dispatch(openEditModal(stock));
    };

    /**
     * Cierra el modal de edición y limpia el producto seleccionado.
     */
    const handlerCloseEditModal = () => {
        dispatch(closeEditModal());
    };


    return {
        list,
        paginator,
        isLoading,
        isEditModalOpen,
        selectedStock,

        handlerGetFilteredStock,
        handlerClearFilteredStock,
        handlerUpdateStockSettings,
        handlerOpenEditModal,
        handlerCloseEditModal,
    };
};
